import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import Icon from 'assets/components/icon/Icon';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import useApi from 'assets/hooks/api/useApi';
import { getLocales, Language } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { preventDefaultBehavior } from 'assets/utils/dom/Events';
import { xor } from 'lodash';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import { useMemo, useRef, useState, useEffect } from 'react';
import { useProductDocumentProvider } from '../../ProductDocument.Hooks';
import { useProductDealerDocumentProvider } from '../ProductDocument.DealerDocuments.Hooks';

export interface ProductDealerDocumentAddForm {
  formFile: File;
  translatedFile: File;
  dealers: number[];
  productId: number;
  productDocumentId: number;
}

export default function ProductDealerDocumentAdd() {
  const { lang } = getLocales();
  const { productDocument } = useProductDocumentProvider();
  const { fullDealerDocumentList, handleUploadDealerDocumentSubmit, setView } = useProductDealerDocumentProvider();

  const dealerListApi = useApi({
    action: Dealer.list,
    body: {
      isInactive: false,
      productId: productDocument?.productId ? [productDocument.productId] : null,
    },
  });

  const availableDealers = useMemo<Dealer[]>(() => {
    const dealerData = dealerListApi.payload?.data;
    if (!dealerData?.length) {
      return [];
    }

    if (fullDealerDocumentList?.length > 0) {
      return dealerData.filter((dealer) => !fullDealerDocumentList.some((doc) => doc.dealerId === dealer.id));
    }

    return dealerData;
  }, [dealerListApi.payload?.data, fullDealerDocumentList]);

  const fileInput = useRef<HTMLInputElement>();
  const [languageIsoCode, setLanguageIsoCode] = useState<{ code: Language | undefined }>({ code: undefined });
  useEffect(() => {
    if (languageIsoCode.code) {
      fileInput?.current.click();
    }
  }, [languageIsoCode]);

  return (
    <ModalForm<ProductDealerDocumentAddForm>
      subcomponents={ThemeModalFormDefault}
      dependencyArray={[productDocument]}
      title={lang.upload}
      default={{ productDocumentId: productDocument?.id, productId: productDocument?.productId }}
      validation={(data) => ({
        dealers: data.dealers?.length ? undefined : lang.required,
        formFile: data.formFile ? undefined : lang.required,
      })}
      onCancel={() => setView(undefined)}
      onSubmit={(data) => handleUploadDealerDocumentSubmit(data)}
      submitButtonText={lang.upload}
      render={({ form }) => (
        <>
          <input
            ref={fileInput}
            onClick={(event) => event.stopPropagation()}
            onChange={(event) => {
              preventDefaultBehavior(event);

              const formFile = event.currentTarget.files[0];
              if (languageIsoCode.code === Language.english) {
                form.update({ formFile });
              } else {
                form.update({ translatedFile: formFile });
              }
              event.currentTarget.value = '';
            }}
            type="file"
            max={1}
            accept="*"
            multiple={false}
            style={{ display: 'none' }}
          />
          <Box style={{ display: 'flex', justifyContent: 'flex-end', padding: Theme.Size.S }}>
            <Button
              class={ThemeButtonCircle}
              style={{ display: 'inline-flex', color: !form.data.formFile ? undefined : Theme.Color.success }}
              media={<Icon class={form.data.formFile ? 'fas-check' : 'fas-upload'} />}
              data={{ code: Language.english }}
              onClick={setLanguageIsoCode}
            >
              {lang.en}
            </Button>
            <Button
              class={ThemeButtonCircle}
              style={{
                display: 'inline-flex',
                color: !form.data.translatedFile ? undefined : Theme.Color.success,
                marginRight: 'auto',
              }}
              media={<Icon class={form.data.translatedFile ? 'fas-check' : 'fas-upload'} />}
              data={{ code: Language.french }}
              disabled={!form.data.formFile}
              onClick={setLanguageIsoCode}
            >
              {lang.fr}
            </Button>
            <Button
              class={{ button: ThemeButtonCircle }}
              media={form.data.dealers?.length === availableDealers?.length ? 'fas-minus-square' : 'fas-check-square'}
              onClick={form.update}
              data={{
                dealers:
                  !availableDealers || form.data.dealers?.length === availableDealers?.length
                    ? []
                    : [...availableDealers.map((x) => x.id)],
              }}
              active={form.data.dealers?.length === availableDealers?.length}
              htmlElementProps={{ title: lang.selectAll }}
              render={lang.selectAll}
            />
          </Box>
          <table className={ThemeTableSimple}>
            <thead>
              <tr>
                <th children={lang.dealer} />
                <th children={lang.actions} style={{ textAlign: 'center' }} />
              </tr>
            </thead>
            <tbody>
              <DataMap
                data={availableDealers}
                render={({ data }) => {
                  const isItemSelected = form.data.dealers?.find((it) => it === data.id);

                  return (
                    <tr key={data.id}>
                      <td children={data.displayInfo.title} />
                      <td style={{ textAlign: 'center' }}>
                        <Button
                          data={data}
                          onClick={() => {
                            form.update({ dealers: xor(form.data.dealers, [data.id]) });
                          }}
                          class={ThemeButtonCircle}
                          media={<Icon class={isItemSelected ? 'fas-check-square' : 'far-square'} />}
                          htmlElementProps={{ title: isItemSelected ? lang.unselect : lang.select }}
                        />
                      </td>
                    </tr>
                  );
                }}
              />
            </tbody>
          </table>
        </>
      )}
    />
  );
}
